import { FC, useMemo } from 'react';

import {
    Box,
    Chip,
    ChipMenu,
    Stack,
    Typography,
} from '@thirdspacelearning/library';
import { capitalise } from '@tsl-frontend/tsl-utilities';

import { ProgrammeStatusType, ProgrammeType } from '../../types';
import { statusMap } from '../../utils';

import { Country } from '@types';

type Props = {
    programme: ProgrammeType;
    setSelectedStatus: (status: ProgrammeStatusType) => void;
};

const ProgrammeInfo: FC<Props> = ({ programme, setSelectedStatus }) => {
    const onStatusChange = (status: string | number) => {
        if (status === programme?.status) {
            return;
        }

        setSelectedStatus(status as ProgrammeStatusType);
    };

    const programmeStatusOptions = useMemo(() => {
        return Object.entries(ProgrammeStatusType).map(([key, value]) => ({
            id: value,
            label: capitalise(value),
            color: statusMap[value],
        }));
    }, []);

    const chips = useMemo(() => {
        if (!programme) {
            return [];
        }

        const chips =
            programme.country === Country.GB
                ? [
                      <Chip
                          key="key_stage"
                          label={`Key stage ${programme.key_stage || '-'}`}
                          variant="outlined"
                          data-testid="chip-key-stage"
                      />,
                      <Chip
                          key="yearId"
                          label={`Year ${programme.year_id || '-'}`}
                          variant="outlined"
                          data-testid="chip-year-id"
                      />,
                  ]
                : [
                      <Chip
                          key="stateStandard"
                          label={programme.state_standard || '-'}
                          variant="outlined"
                          data-testid="chip-state-standard"
                      />,
                      <Chip
                          key="grade"
                          label={`Grade ${programme.year_id || '-'}`}
                          variant="outlined"
                          data-testid="chip-grade"
                      />,
                  ];

        return chips.filter(Boolean);
    }, [programme]);

    return (
        <Stack gap={1}>
            <Box display="flex" gap={1}>
                <Chip
                    label={`ID: ${programme.id}`}
                    variant="outlined"
                    data-testid="chip-id"
                />
                {chips}
                {programme.ai && (
                    <Chip
                        label={`AI Programme`}
                        variant="outlined"
                        data-testid="chip-ai"
                    />
                )}
                <ChipMenu
                    items={programmeStatusOptions}
                    onChange={onStatusChange}
                    placeholder={'No status'}
                    value={programme.status as string}
                    slotProps={{
                        chip: {
                            'data-testid': 'programme-chip-menu',
                        },
                    }}
                />
            </Box>
            <Typography variant="body1" maxWidth="900px" color="text.secondary">
                {programme.description}
            </Typography>
        </Stack>
    );
};

export default ProgrammeInfo;
